import React from "react";
import { availableInventoryUnits, inventoryMatches } from "../../lib/inventorySelection";
import SearchableSelect from "../common/SearchableSelect";
import { AudioVisualizer } from "react-audio-visualize";

import {
  DOCUMENT_ALLOWED_EXTENSIONS,
  LEAD_STATUS_SEQUENCE,
  MAX_LEAD_DOCUMENTS,
} from "../../lib/constants";
import {
  CalendarIcon,
  CloseIcon,
  DocumentIcon,
  PhoneIcon,
  UserGroupIcon,
} from "../../lib/icons";
import {
  displayPhone,
  formatDateCell,
  startCase,
  toInputDateTime,
  validateDocumentSelection,
} from "../../lib/utils";

// ── Inline SVG icons ─────────────────────────────────────────
function ChevronDownIcon({ size = 14 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 20 20" fill="currentColor">
      <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
    </svg>
  );
}
function ChevronUpIcon({ size = 14 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 20 20" fill="currentColor">
      <path fillRule="evenodd" d="M14.707 12.707a1 1 0 01-1.414 0L10 9.414l-3.293 3.293a1 1 0 01-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z" clipRule="evenodd" />
    </svg>
  );
}
function TransferIcon() {
  return (
    <svg viewBox="0 0 20 20" fill="currentColor">
      <path d="M8 5a1 1 0 100 2h5.586l-1.293 1.293a1 1 0 001.414 1.414l3-3a1 1 0 000-1.414l-3-3a1 1 0 10-1.414 1.414L13.586 5H8zM12 15a1 1 0 100-2H6.414l1.293-1.293a1 1 0 10-1.414-1.414l-3 3a1 1 0 000 1.414l3 3a1 1 0 001.414-1.414L6.414 15H12z" />
    </svg>
  );
}
function WhatsAppIcon({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" style={{ flexShrink: 0 }}>
      <path d="M.057 24l1.687-6.163a11.867 11.867 0 01-1.587-5.945C.16 5.335 5.495 0 12.05 0a11.817 11.817 0 018.413 3.488 11.824 11.824 0 013.48 8.414c-.003 6.557-5.338 11.892-11.893 11.892a11.9 11.9 0 01-5.688-1.448L.057 24zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884a9.86 9.86 0 001.51 5.26l-.999 3.648 3.978-1.607zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413z"/>
    </svg>
  );
}
function NoteIcon() {
  return (
    <svg viewBox="0 0 20 20" fill="currentColor">
      <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clipRule="evenodd" />
    </svg>
  );
}
function TagIcon() {
  return (
    <svg viewBox="0 0 20 20" fill="currentColor">
      <path fillRule="evenodd" d="M17.707 9.293a1 1 0 010 1.414l-7 7a1 1 0 01-1.414 0l-7-7A.997.997 0 012 10V5a3 3 0 013-3h5c.256 0 .512.098.707.293l7 7zM5 6a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
    </svg>
  );
}

// ── Avatar color from name ────────────────────────────────────
const AVATAR_PALETTES = [
  { bg: "#dbeafe", fg: "#1d4ed8" },
  { bg: "#dcfce7", fg: "#15803d" },
  { bg: "#ede9fe", fg: "#7c3aed" },
  { bg: "#fef3c7", fg: "#b45309" },
  { bg: "#fce7f3", fg: "#be185d" },
  { bg: "#cffafe", fg: "#0e7490" },
];

function avatarPalette(name) {
  if (!name) return AVATAR_PALETTES[0];
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) | 0;
  return AVATAR_PALETTES[Math.abs(h) % AVATAR_PALETTES.length];
}

function initials(name) {
  if (!name) return "?";
  return name.split(" ").filter(Boolean).map((w) => w[0]).join("").slice(0, 2).toUpperCase();
}

// ── Status stepper ────────────────────────────────────────────
const STATUS_STEPS = LEAD_STATUS_SEQUENCE.map((step) => ({
  key: step.value,
  label: step.label,
}));
const CONTACT_QUALIFIED_CALL_MIN_SECONDS = 10;

function stepIndex(status) {
  const i = STATUS_STEPS.findIndex((s) => s.key === status);
  return i === -1 ? 0 : i;
}

function statusLabelForKey(statusKey) {
  return STATUS_STEPS.find((step) => step.key === statusKey)?.label || startCase(statusKey);
}

function isConnectedAttempt(callLog) {
  const status = String(callLog?.status || "").toLowerCase();
  return status === "connected" || status === "completed" || (callLog?.duration_seconds || 0) > 0;
}

function isContactQualifiedAttempt(callLog) {
  return isConnectedAttempt(callLog) && (callLog?.duration_seconds || 0) >= CONTACT_QUALIFIED_CALL_MIN_SECONDS;
}

function matchesStageStatusEvent(activity, step) {
  if (!activity || activity.activity_type !== "status_change") {
    return false;
  }
  const toStatus = String(activity.metadata?.to || "").toLowerCase();
  if (toStatus === step.key) {
    return true;
  }
  const label = step.label.toLowerCase();
  const title = String(activity.title || "").toLowerCase();
  const description = String(activity.description || "").toLowerCase();
  return title.includes(label) || description.includes(label);
}

function stageEventRows(step, lead, activities) {
  const matched = activities.filter((activity) => matchesStageStatusEvent(activity, step));
  if (matched.length > 0) {
    return matched;
  }
  if (lead.status === step.key) {
    return [{
      id: `current-${step.key}`,
      occurred_at: lead.last_follow_up_at || lead.last_contacted_at || lead.next_follow_up_at || lead.registered_at || lead.created_at,
      title: `Current stage: ${step.label}`,
      description: "This lead is currently in this stage, but no separate stage-transition history is recorded yet.",
      metadata: {
        location: lead.geography_name || lead.city_name || "",
      },
      actor_name: lead.assigned_agent?.full_name || "",
    }];
  }
  return [];
}

function StageDetailsModal({ lead, activities, callLogs, step, onClose }) {
  if (!step) return null;

  const stageActivities = stageEventRows(step, lead, activities);
  const connectedCalls = callLogs.filter(isConnectedAttempt);
  const contactedCalls = callLogs.filter(isContactQualifiedAttempt);
  const locationFallback = lead.geography_name || lead.city_name || "Not recorded";

  function renderStatusHistoryRows(rows, emptyMessage, countLabel) {
    return (
      <>
        <div className="lj-stage-summary-grid">
          <div className="lj-stage-summary-card">
            <span className="lj-stage-summary-label">{countLabel}</span>
            <strong className="lj-stage-summary-value">{rows.length}</strong>
          </div>
        </div>
        {rows.length ? (
          <div className="lj-stage-event-list">
            {rows.map((row) => {
              const location = row.metadata?.dealership
                || row.metadata?.location
                || row.metadata?.city_name
                || locationFallback;
              return (
                <div className="lj-stage-event-card" key={row.id}>
                  <div className="lj-stage-event-head">
                    <strong>{row.title || step.label}</strong>
                    <span>{formatDateCell(row.occurred_at)}</span>
                  </div>
                  <div className="lj-stage-event-meta">
                    <span>Location: {location || "Not recorded"}</span>
                    {row.actor_name ? <span>Updated by: {row.actor_name}</span> : null}
                  </div>
                  {row.description ? <p className="lj-stage-event-desc">{row.description}</p> : null}
                </div>
              );
            })}
          </div>
        ) : (
          <p className="lj-empty">{emptyMessage}</p>
        )}
      </>
    );
  }

  function renderContactedContent() {
    const interactionActivities = activities.filter((activity) =>
      activity.activity_type === "call"
      || activity.activity_type === "disposition_change"
      || matchesStageStatusEvent(activity, { key: "contacted", label: "Contacted" }),
    );

    const connectionRate = callLogs.length
      ? Math.round((connectedCalls.length / callLogs.length) * 100)
      : 0;

    return (
      <>
        {/* ── KPI strip ── */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 12, marginBottom: 18 }}>
          {[
            { label: "Times Contacted",   value: contactedCalls.length, accent: "#2563eb", bg: "#eff6ff" },
            { label: "Connected Attempts", value: connectedCalls.length, accent: "#16a34a", bg: "#f0fdf4" },
            { label: "All Call Attempts",  value: callLogs.length,       accent: "#7c3aed", bg: "#f5f3ff" },
          ].map(({ label, value, accent, bg }) => (
            <div key={label} style={{
              background: bg, border: `1.5px solid ${accent}22`,
              borderRadius: 12, padding: "14px 16px",
            }}>
              <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: accent, opacity: 0.8 }}>{label}</span>
              <div style={{ fontSize: 28, fontWeight: 800, color: accent, marginTop: 4 }}>{value}</div>
            </div>
          ))}
        </div>

        {/* ── Connection rate bar ── */}
        {callLogs.length > 0 && (
          <div style={{ marginBottom: 18, background: "#f8faff", border: "1.5px solid #e0e7ff", borderRadius: 10, padding: "10px 14px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
              <span style={{ fontSize: 12, fontWeight: 600, color: "#4b5563" }}>Connection Rate</span>
              <span style={{ fontSize: 13, fontWeight: 700, color: connectionRate >= 50 ? "#16a34a" : "#dc2626" }}>{connectionRate}%</span>
            </div>
            <div style={{ height: 6, background: "#e5e7eb", borderRadius: 999 }}>
              <div style={{ height: 6, width: `${connectionRate}%`, background: connectionRate >= 50 ? "#22c55e" : "#f87171", borderRadius: 999, transition: "width 0.4s" }} />
            </div>
          </div>
        )}

        {/* ── Call attempts ── */}
        <div className="lj-stage-subsection">
          <div className="lj-section-title" style={{ marginBottom: 10 }}>Call Attempts</div>
          {callLogs.length ? (
            <div className="lj-stage-event-list">
              {callLogs.map((call) => {
                const connected = isConnectedAttempt(call);
                return (
                  <div className="lj-stage-event-card" key={call.id} style={{ borderLeft: `3px solid ${connected ? "#22c55e" : "#f87171"}` }}>
                    <div className="lj-stage-event-head">
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <strong>{call.agent_name || "Agent"}</strong>
                        <span style={{
                          fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 999,
                          background: connected ? "#dcfce7" : "#fee2e2",
                          color: connected ? "#15803d" : "#dc2626",
                        }}>
                          {connected ? "Connected" : "Unconnected"}
                        </span>
                      </div>
                      <span style={{ color: "#6b7280", fontSize: 12 }}>{formatDateCell(call.started_at)}</span>
                    </div>
                    <div className="lj-stage-event-meta">
                      <span>State: {startCase(call.status || "unknown")}</span>
                      <span>Duration: {formatDur(call.duration_seconds) || "0:00"}</span>
                    </div>
                    {call.remarks ? <p className="lj-stage-event-desc">{call.remarks}</p> : null}
                    {call.recording_url ? <CallRecordingPlayer call={call} leadId={lead.id} /> : null}
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="lj-empty">No call attempts are recorded for this lead yet.</p>
          )}
        </div>

        <div className="lj-stage-subsection">
          <div className="lj-section-title" style={{ marginBottom: 10 }}>Interaction History</div>
          {interactionActivities.length ? (
            <ActivityTimeline activities={interactionActivities} lead={lead} />
          ) : (
            <p className="lj-empty">No contacted-stage interaction history recorded yet.</p>
          )}
        </div>
      </>
    );
  }

  function renderScheduledContent() {
    const cf = lead.custom_fields || {};


    const fullHistory = Array.isArray(lead.test_drive_history_full) && lead.test_drive_history_full.length > 0
      ? lead.test_drive_history_full
      : null;

    const rawHistory = fullHistory
      || (Array.isArray(cf.test_drive_history) && cf.test_drive_history.length > 0
          ? [...cf.test_drive_history].sort((a, b) => new Date(b.booked_at || b.date || 0) - new Date(a.booked_at || a.date || 0))
          : null)
      || ((cf.test_drive_date || cf.test_drive_oem || cf.test_drive_dealer)
          ? [{ date: cf.test_drive_date || "", oem: cf.test_drive_oem || "", dealer: cf.test_drive_dealer || "", city: cf.test_drive_city || lead.city_name || "", booked_at: "", _legacy: true }]
          : []);

    // Already sorted newest-first by the backend; ensure it here too.
    const displayHistory = [...rawHistory].sort((a, b) => new Date(b.booked_at || b.date || 0) - new Date(a.booked_at || a.date || 0));

    // Use most-recent booking from history as fallback (covers prod leads with audit-log-recovered data)
    const latestEntry     = displayHistory[0] || null;
    const latestOem       = cf.test_drive_oem        || latestEntry?.oem        || "—";
    const latestDealer    = cf.test_drive_dealer      || latestEntry?.dealer     || "—";
    const latestCity      = cf.test_drive_city        || latestEntry?.city       || lead.city_name || lead.geography_name || "—";
    const latestDate      = cf.test_drive_date        || latestEntry?.date       || null;
    const latestAgentName = cf.test_drive_agent_name  || latestEntry?.agent_name || lead.assigned_agent?.full_name || "—";

    const timesScheduled = parseInt(cf.test_drive_count || "0", 10) || displayHistory.length || stageActivities.length || 0;

    return (
      <>
        {/* ══ STICKY TOP SECTION — never scrolls ══ */}
        <div style={{ flexShrink: 0 }}>
          {/* Times scheduled badge */}
          <div style={{
            background: "#eff6ff", border: "1.5px solid #2563eb22",
            borderRadius: 12, padding: "14px 18px",
            display: "flex", alignItems: "center", justifyContent: "space-between",
            marginBottom: 18,
          }}>
            <span style={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: "#2563eb", opacity: 0.8 }}>
              Times Scheduled
            </span>
            <span style={{ fontSize: 30, fontWeight: 800, color: "#2563eb" }}>{timesScheduled}</span>
          </div>

          {/* Latest booking details card */}
          <div style={{ background: "#fff", border: "1.5px solid #e0e7ff", borderRadius: 12, overflow: "hidden" }}>
            <div style={{ padding: "10px 16px", background: "linear-gradient(135deg,#2563eb,#1d4ed8)", color: "#fff", fontSize: 12, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase" }}>
              Latest Test Drive Details
            </div>
            <div style={{ padding: "4px 0" }}>
              {[
                { icon: "📅", label: "Schedule Date", value: latestDate ? formatDateCell(latestDate) : "—", accent: latestDate ? "#2563eb" : "#9ca3af", bold: true },
                { icon: "🏭", label: "OEM",            value: latestOem },
                { icon: "🏢", label: "Dealer",         value: latestDealer },
                { icon: "📍", label: "City",           value: latestCity },
                { icon: "👤", label: "Agent",          value: latestAgentName },
              ].map(({ icon, label, value, accent, bold }, i, arr) => (
                <div key={label} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "11px 16px", borderBottom: i < arr.length - 1 ? "1px solid #f1f5f9" : "none" }}>
                  <span style={{ fontSize: 13, color: "#6b7280", display: "flex", alignItems: "center", gap: 8 }}>
                    <span>{icon}</span>{label}
                  </span>
                  <span style={{ fontSize: 13, fontWeight: bold ? 700 : 600, color: accent || "#111827" }}>{value}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ══ SCROLLABLE HISTORY SECTION — fills remaining modal height ══ */}
        <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
          <div className="lj-section-title" style={{ marginBottom: 10, flexShrink: 0 }}>Schedule History</div>

          {/* Only this list scrolls */}
          <div style={{ flex: 1, minHeight: 0, overflowY: "auto", paddingRight: 2 }}>
            {displayHistory.length > 0 ? displayHistory.map((booking, idx) => (
              <div key={booking.booked_at || idx} style={{
                background: idx === 0 ? "#eff6ff" : "#fafafa",
                border: `1.5px solid ${idx === 0 ? "#2563eb33" : "#e5e7eb"}`,
                borderLeft: `3px solid ${idx === 0 ? "#2563eb" : "#93c5fd"}`,
                borderRadius: 10, padding: "12px 14px", marginBottom: 8,
              }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                  <span style={{ fontWeight: 700, fontSize: 13, color: idx === 0 ? "#1d4ed8" : "#374151" }}>
                    {booking._legacy
                      ? `Booking #${timesScheduled} (latest)`
                      : `Booking #${displayHistory.length - idx}`}
                  </span>
                  <span style={{ fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 999, background: idx === 0 ? "#dbeafe" : "#f3f4f6", color: idx === 0 ? "#1d4ed8" : "#6b7280" }}>
                    {booking.date ? formatDateCell(booking.date) : "—"}
                  </span>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "6px 12px" }}>
                  {[
                    { icon: "🏭", label: "OEM",       value: booking.oem        || "—" },
                    { icon: "🏢", label: "Dealer",    value: booking.dealer     || "—" },
                    { icon: "📍", label: "City",      value: booking.city       || "—" },
                    { icon: "👤", label: "Agent",     value: booking.agent_name || "—" },
                    { icon: "🕐", label: "Booked at", value: !booking._legacy && booking.booked_at ? formatDateCell(booking.booked_at) : "—" },
                  ].map(({ icon, label, value }) => (
                    <div key={label} style={{ display: "flex", gap: 5, alignItems: "center", fontSize: 12 }}>
                      <span>{icon}</span>
                      <span style={{ color: "#9ca3af" }}>{label}:</span>
                      <span style={{ color: "#111827", fontWeight: 600 }}>{value}</span>
                    </div>
                  ))}
                </div>
              </div>
            )) : (
              <p style={{ fontSize: 13, color: "#9ca3af", margin: 0 }}>No booking history yet.</p>
            )}
          </div>
        </div>
      </>
    );
  }

  function renderConductedContent() {
    const cf = lead.custom_fields || {};

    const rawHistory = Array.isArray(cf.test_drive_conducted_history) && cf.test_drive_conducted_history.length > 0
      ? [...cf.test_drive_conducted_history].sort((a, b) => new Date(b.conducted_at || 0) - new Date(a.conducted_at || 0))
      : [];

    const latest = cf.test_drive_conducted_latest || (rawHistory.length > 0 ? rawHistory[0] : null);
    const conductedCount = parseInt(cf.test_drive_conducted_count || "0", 10) || rawHistory.length || 0;

    // Scheduled drives sorted newest-first — use audit-log-recovered history when custom_fields is sparse
    const tdScheduledRaw = (
      (Array.isArray(lead.test_drive_history_full) && lead.test_drive_history_full.length > 0)
        ? lead.test_drive_history_full
        : (Array.isArray(cf.test_drive_history) ? cf.test_drive_history : [])
    );
    const tdScheduled = [...tdScheduledRaw].sort((a, b) => new Date(b.booked_at || 0) - new Date(a.booked_at || 0));

    // Find the last scheduled drive booked before (or at) a conducted_at timestamp
    function scheduledRefFor(conductedAt) {
      const t = new Date(conductedAt || 0);
      return tdScheduled.find(h => new Date(h.booked_at || 0) <= t) || tdScheduled[0] || null;
    }

    const latestRef  = scheduledRefFor(latest?.conducted_at);
    const latestOem    = latest?.oem          || latestRef?.oem    || "—";
    const latestDealer = latest?.dealer        || latestRef?.dealer || "—";
    const latestCity   = latest?.city          || latestRef?.city   || lead.city_name || lead.geography_name || "—";
    const latestDate   = latest?.schedule_date || latestRef?.date   || null;
    const latestAgent  = latest?.agent_name    || lead.assigned_agent?.full_name || "—";
    const latestSat    = latest?.satisfied;

    const satisfactionBadge = (sat) => {
      if (sat === true)  return { label: "Satisfied",     bg: "#dcfce7", color: "#15803d", border: "#86efac" };
      if (sat === false) return { label: "Not Satisfied", bg: "#fee2e2", color: "#b91c1c", border: "#fca5a5" };
      return { label: "—", bg: "#f3f4f6", color: "#6b7280", border: "#e5e7eb" };
    };

    return (
      <>
        {/* ══ STICKY TOP SECTION ══ */}
        <div style={{ flexShrink: 0 }}>
          {/* Times conducted badge */}
          <div style={{
            background: "#f0fdf4", border: "1.5px solid #16a34a22",
            borderRadius: 12, padding: "14px 18px",
            display: "flex", alignItems: "center", justifyContent: "space-between",
            marginBottom: 18,
          }}>
            <span style={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: "#16a34a", opacity: 0.9 }}>
              Test Drives Conducted
            </span>
            <span style={{ fontSize: 30, fontWeight: 800, color: "#16a34a" }}>{conductedCount}</span>
          </div>

          {/* Latest conducted details card */}
          {latest ? (
            <div style={{ background: "#fff", border: "1.5px solid #bbf7d0", borderRadius: 12, overflow: "hidden", marginBottom: 18 }}>
              <div style={{ padding: "10px 16px", background: "linear-gradient(135deg,#16a34a,#15803d)", color: "#fff", fontSize: 12, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase" }}>
                Latest Conducted Drive
              </div>
              <div style={{ padding: "4px 0" }}>
                {(() => {
                  const { label: satLabel, bg: satBg, color: satColor, border: satBorder } = satisfactionBadge(latestSat);
                  return [
                    { icon: "📅", label: "Drive Date",   value: latestDate ? formatDateCell(latestDate) : "—", accent: latestDate ? "#16a34a" : "#9ca3af", bold: true },
                    { icon: "🏭", label: "OEM",           value: latestOem },
                    { icon: "🏢", label: "Dealer",        value: latestDealer },
                    { icon: "📍", label: "City",          value: latestCity },
                    { icon: "👤", label: "Agent",         value: latestAgent },
                    { icon: "✅", label: "Satisfaction",  value: null, satBadge: { label: satLabel, bg: satBg, color: satColor, border: satBorder } },
                  ].map(({ icon, label, value, accent, bold, satBadge }, i, arr) => (
                    <div key={label} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "11px 16px", borderBottom: i < arr.length - 1 ? "1px solid #f1f5f9" : "none" }}>
                      <span style={{ fontSize: 13, color: "#6b7280", display: "flex", alignItems: "center", gap: 8 }}>
                        <span>{icon}</span>{label}
                      </span>
                      {satBadge ? (
                        <span style={{ fontSize: 12, fontWeight: 700, padding: "3px 10px", borderRadius: 999, background: satBadge.bg, color: satBadge.color, border: `1px solid ${satBadge.border}` }}>
                          {satBadge.label}
                        </span>
                      ) : (
                        <span style={{ fontSize: 13, fontWeight: bold ? 700 : 600, color: accent || "#111827" }}>{value}</span>
                      )}
                    </div>
                  ));
                })()}
              </div>
            </div>
          ) : (
            <p style={{ fontSize: 13, color: "#9ca3af", marginBottom: 18 }}>No conducted drive details recorded yet.</p>
          )}
        </div>

        {/* ══ SCROLLABLE HISTORY SECTION ══ */}
        <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
          <div className="lj-section-title" style={{ marginBottom: 10, flexShrink: 0 }}>Conducted History</div>

          <div style={{ flex: 1, minHeight: 0, overflowY: "auto", paddingRight: 2 }}>
            {rawHistory.length > 0 ? rawHistory.map((entry, idx) => {
              const { label: satLabel, bg: satBg, color: satColor, border: satBorder } = satisfactionBadge(entry.satisfied);
              // Fill missing fields from the matching scheduled drive
              const entryRef = scheduledRefFor(entry.conducted_at);
              const entryOem    = entry.oem          || entryRef?.oem    || "—";
              const entryDealer = entry.dealer        || entryRef?.dealer || "—";
              const entryCity   = entry.city          || entryRef?.city   || "—";
              const entryDate   = entry.schedule_date || entryRef?.date   || null;
              return (
                <div key={entry.conducted_at || idx} style={{
                  background: idx === 0 ? "#f0fdf4" : "#fafafa",
                  border: `1.5px solid ${idx === 0 ? "#16a34a33" : "#e5e7eb"}`,
                  borderLeft: `3px solid ${idx === 0 ? "#16a34a" : "#86efac"}`,
                  borderRadius: 10, padding: "12px 14px", marginBottom: 8,
                }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                    <span style={{ fontWeight: 700, fontSize: 13, color: idx === 0 ? "#15803d" : "#374151" }}>
                      Drive #{rawHistory.length - idx}
                    </span>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <span style={{ fontSize: 12, fontWeight: 700, padding: "2px 8px", borderRadius: 999, background: satBg, color: satColor, border: `1px solid ${satBorder}` }}>
                        {satLabel}
                      </span>
                      <span style={{ fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 999, background: idx === 0 ? "#dcfce7" : "#f3f4f6", color: idx === 0 ? "#15803d" : "#6b7280" }}>
                        {entry.conducted_at ? formatDateCell(entry.conducted_at) : "—"}
                      </span>
                    </div>
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "6px 12px" }}>
                    {[
                      { icon: "📅", label: "Drive Date", value: entryDate ? formatDateCell(entryDate) : "—" },
                      { icon: "🏭", label: "OEM",        value: entryOem },
                      { icon: "🏢", label: "Dealer",     value: entryDealer },
                      { icon: "📍", label: "City",       value: entryCity },
                      { icon: "👤", label: "Agent",      value: entry.agent_name || "—" },
                    ].map(({ icon, label, value }) => (
                      <div key={label} style={{ display: "flex", gap: 5, alignItems: "center", fontSize: 12 }}>
                        <span>{icon}</span>
                        <span style={{ color: "#9ca3af" }}>{label}:</span>
                        <span style={{ color: "#111827", fontWeight: 600 }}>{value}</span>
                      </div>
                    ))}
                  </div>
                </div>
              );
            }) : (
              <p style={{ fontSize: 13, color: "#9ca3af", margin: 0 }}>No conducted drive history recorded yet.</p>
            )}
          </div>
        </div>
      </>
    );
  }

  function renderBookedOrDeliveredContent(isDelivered) {
    // ── theme tokens ──────────────────────────────────────────────
    const accent        = isDelivered ? "#7c3aed" : "#d97706";
    const accentDark    = isDelivered ? "#6d28d9" : "#b45309";
    const accentBg      = isDelivered ? "#f5f3ff" : "#fffbeb";
    const accentBgDeep  = isDelivered ? "#ede9fe" : "#fef3c7";
    const accentBorder  = isDelivered ? "#ddd6fe" : "#fde68a";
    const accentLight   = isDelivered ? "#c4b5fd" : "#fcd34d";
    const accentText    = isDelivered ? "#6d28d9" : "#92400e";
    const milestoneIcon = isDelivered ? "🚗" : "📋";
    const cardTitle     = isDelivered ? "Delivery Details" : "Booking Details";
    const histTitle     = isDelivered ? "Delivery History" : "Booking History";
    const stageLabel    = isDelivered ? "Delivered" : "Booked";
    const emptyMsg      = isDelivered
      ? "No delivery history is recorded for this lead yet."
      : "No booking history is recorded for this lead yet.";

    // ── data ──────────────────────────────────────────────────────
    const cf = lead.custom_fields || {};

    // Dedicated booking / delivery history (newest-first)
    const cfHistory = isDelivered
      ? (Array.isArray(cf.delivery_history) ? [...cf.delivery_history] : [])
      : (Array.isArray(cf.booking_history)  ? [...cf.booking_history]  : []);
    cfHistory.sort((a, b) =>
      new Date(b.delivered_at || b.booked_at || 0) - new Date(a.delivered_at || a.booked_at || 0)
    );

    // Latest entry: prefer dedicated history, fall back to top-level custom_fields
    const latestCf = cfHistory[0] || null;
    const oem    = latestCf?.oem    || (isDelivered ? cf.delivery_oem    : cf.booking_oem)    || cf.test_drive_oem    || "—";
    const dealer = latestCf?.dealer || (isDelivered ? cf.delivery_dealer : cf.booking_dealer) || cf.test_drive_dealer || "—";
    const city   = latestCf?.city   || (isDelivered ? cf.delivery_city   : cf.booking_city)   || lead.city_name || lead.geography_name || "—";
    const cfDate = latestCf?.date   || (isDelivered ? cf.delivery_date   : cf.booking_date)   || null;
    const cfAgent = latestCf?.agent_name || (isDelivered ? cf.delivery_agent_name : cf.booking_agent_name) || "";

    // status_change activities (for when/who changed the stage)
    const stageEntries = stageActivities
      .slice()
      .sort((a, b) => new Date(b.occurred_at || 0) - new Date(a.occurred_at || 0));
    const latestActivity = stageEntries[0] || null;

    // Use cfHistory if it has data, otherwise fall back to status_change activities for history list
    const hasDetailedHistory = cfHistory.length > 0;

    const isCurrentStage = lead.status === (isDelivered ? "delivered" : "booked");

    return (
      <>
        {/* ══ STICKY TOP ══ */}
        <div style={{ flexShrink: 0 }}>

          {/* Milestone strip */}
          <div style={{
            background: accentBg, border: `1.5px solid ${accentBorder}`,
            borderRadius: 12, padding: "14px 16px",
            display: "flex", alignItems: "center", gap: 12, marginBottom: 16,
          }}>
            <div style={{
              width: 42, height: 42, borderRadius: "50%",
              background: `linear-gradient(135deg, ${accent}, ${accentDark})`,
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 20, flexShrink: 0, boxShadow: `0 2px 8px ${accent}44`,
            }}>
              {milestoneIcon}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: "#111827" }}>
                {stageLabel}
                {(cfDate || latestActivity?.occurred_at) ? (
                  <span style={{ fontWeight: 400, color: "#6b7280", marginLeft: 6 }}>
                    on {formatDateCell(cfDate || latestActivity?.occurred_at)}
                  </span>
                ) : null}
              </div>
              {(cfAgent || latestActivity?.actor_name) && (
                <div style={{ fontSize: 12, color: "#6b7280", marginTop: 2 }}>
                  by {cfAgent || latestActivity?.actor_name}
                </div>
              )}
            </div>
            {isCurrentStage && (
              <span style={{
                flexShrink: 0, fontSize: 11, fontWeight: 700,
                padding: "4px 10px", borderRadius: 999,
                background: accent, color: "#fff", letterSpacing: "0.04em",
              }}>Current Stage</span>
            )}
          </div>

          {/* Details card */}
          <div style={{ background: "#fff", border: `1.5px solid ${accentBorder}`, borderRadius: 12, overflow: "hidden", marginBottom: 16 }}>
            <div style={{
              padding: "10px 16px",
              background: `linear-gradient(135deg, ${accent}, ${accentDark})`,
              color: "#fff", fontSize: 12, fontWeight: 700,
              letterSpacing: "0.06em", textTransform: "uppercase",
            }}>
              {cardTitle}
            </div>
            <div style={{ padding: "4px 0" }}>
              {[
                { icon: "📅", label: isDelivered ? "Delivery Date" : "Booking Date", value: cfDate ? formatDateCell(cfDate) : (latestActivity?.occurred_at ? formatDateCell(latestActivity.occurred_at) : "—"), bold: true, color: (cfDate || latestActivity?.occurred_at) ? accent : "#9ca3af" },
                { icon: "🏭", label: "OEM",    value: oem },
                { icon: "🏢", label: "Dealer", value: dealer },
                { icon: "👤", label: "Agent",  value: cfAgent || latestActivity?.actor_name || lead.assigned_agent?.full_name || "—" },
                { icon: "📍", label: "City",   value: city },
              ].map(({ icon, label, value, bold, color }, i, arr) => (
                <div key={label} style={{
                  display: "flex", alignItems: "center", justifyContent: "space-between",
                  padding: "11px 16px",
                  borderBottom: i < arr.length - 1 ? "1px solid #f1f5f9" : "none",
                }}>
                  <span style={{ fontSize: 13, color: "#6b7280", display: "flex", alignItems: "center", gap: 8 }}>
                    <span>{icon}</span>{label}
                  </span>
                  <span style={{ fontSize: 13, fontWeight: bold ? 700 : 600, color: color || "#111827" }}>{value}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ══ SCROLLABLE HISTORY ══ */}
        <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
          <div className="lj-section-title" style={{ marginBottom: 10, flexShrink: 0 }}>{histTitle}</div>
          <div style={{ flex: 1, minHeight: 0, overflowY: "auto", paddingRight: 2 }}>
            {hasDetailedHistory ? cfHistory.map((entry, idx) => {
              const entryDate = entry.date || entry.delivered_at || entry.booked_at;
              return (
              <div key={entry.booked_at || entry.delivered_at || idx} style={{
                background: idx === 0 ? accentBg : "#fafafa",
                border: `1.5px solid ${idx === 0 ? `${accent}33` : "#e5e7eb"}`,
                borderLeft: `3px solid ${idx === 0 ? accent : accentLight}`,
                borderRadius: 10, padding: "12px 14px", marginBottom: 8,
              }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                  <span style={{ fontWeight: 700, fontSize: 13, color: idx === 0 ? accentText : "#374151" }}>
                    {stageLabel} #{cfHistory.length - idx}
                  </span>
                  <span style={{
                    fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 999,
                    background: idx === 0 ? accentBgDeep : "#f3f4f6",
                    color: idx === 0 ? accentText : "#6b7280",
                  }}>
                    {entryDate ? formatDateCell(entryDate) : "—"}
                  </span>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "6px 12px" }}>
                  {[
                    { icon: "🏭", label: "OEM",    value: entry.oem    || "—" },
                    { icon: "🏢", label: "Dealer", value: entry.dealer || "—" },
                    { icon: "👤", label: "Agent",  value: entry.agent_name || "—" },
                    { icon: "📍", label: "City",   value: entry.city   || "—" },
                  ].map(({ icon, label, value }) => (
                    <div key={label} style={{ display: "flex", gap: 5, alignItems: "center", fontSize: 12 }}>
                      <span>{icon}</span>
                      <span style={{ color: "#9ca3af" }}>{label}:</span>
                      <span style={{ color: "#111827", fontWeight: 600 }}>{value}</span>
                    </div>
                  ))}
                </div>
              </div>
            );
            }) : stageEntries.length > 0 ? stageEntries.map((entry, idx) => (
              <div key={entry.id || entry.occurred_at || idx} style={{
                background: idx === 0 ? accentBg : "#fafafa",
                border: `1.5px solid ${idx === 0 ? `${accent}33` : "#e5e7eb"}`,
                borderLeft: `3px solid ${idx === 0 ? accent : accentLight}`,
                borderRadius: 10, padding: "12px 14px", marginBottom: 8,
              }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                  <span style={{ fontWeight: 700, fontSize: 13, color: idx === 0 ? accentText : "#374151" }}>
                    {stageLabel} #{stageEntries.length - idx}
                  </span>
                  <span style={{ fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 999, background: idx === 0 ? accentBgDeep : "#f3f4f6", color: idx === 0 ? accentText : "#6b7280" }}>
                    {entry.occurred_at ? formatDateCell(entry.occurred_at) : "—"}
                  </span>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "6px 12px" }}>
                  {[
                    { icon: "👤", label: "Agent", value: entry.actor_name || "—" },
                    { icon: "📍", label: "City",  value: city },
                  ].map(({ icon, label, value }) => (
                    <div key={label} style={{ display: "flex", gap: 5, alignItems: "center", fontSize: 12 }}>
                      <span>{icon}</span>
                      <span style={{ color: "#9ca3af" }}>{label}:</span>
                      <span style={{ color: "#111827", fontWeight: 600 }}>{value}</span>
                    </div>
                  ))}
                </div>
              </div>
            )) : (
              <div style={{ textAlign: "center", padding: "32px 16px", background: "#fafafa", borderRadius: 12, border: "1.5px dashed #e5e7eb" }}>
                <div style={{ fontSize: 28, marginBottom: 8 }}>{milestoneIcon}</div>
                <p style={{ fontSize: 13, color: "#9ca3af", margin: 0 }}>{emptyMsg}</p>
              </div>
            )}
          </div>
        </div>
      </>
    );
  }

  function renderGenericContent() {
    if (step.key === "new") {
      return (
        <div className="lj-stage-summary-grid">
          <div className="lj-stage-summary-card">
            <span className="lj-stage-summary-label">Lead Created</span>
            <strong className="lj-stage-summary-value">{formatDateCell(lead.registered_at || lead.created_at)}</strong>
          </div>
          <div className="lj-stage-summary-card">
            <span className="lj-stage-summary-label">Source</span>
            <strong className="lj-stage-summary-value">{lead.source_name || "—"}</strong>
          </div>
          <div className="lj-stage-summary-card">
            <span className="lj-stage-summary-label">Initial Geography</span>
            <strong className="lj-stage-summary-value">{locationFallback}</strong>
          </div>
        </div>
      );
    }

    return renderStatusHistoryRows(
      stageActivities,
      `No ${step.label.toLowerCase()} stage history is recorded for this lead yet.`,
      "Recorded Stage Entries",
    );
  }

  let body = renderGenericContent();
  if (step.key === "contacted") body = renderContactedContent();
  if (step.key === "test_drive_scheduled") body = renderScheduledContent();
  if (step.key === "test_drive_conducted") body = renderConductedContent();
  if (step.key === "booked") body = renderBookedOrDeliveredContent(false);
  if (step.key === "delivered") body = renderBookedOrDeliveredContent(true);

  return (
    <div className="modal-backdrop lj-stage-backdrop" onClick={onClose}>
      <div className="modal-panel lj-stage-panel" onClick={(event) => event.stopPropagation()}>
        <div className="lj-stage-modal-head">
          <div>
            <p className="modal-kicker">Lead Stage</p>
            <h2 className="lj-stage-modal-title">{step.label}</h2>
          </div>
          <button type="button" className="close-button" onClick={onClose}><CloseIcon /></button>
        </div>
        <div className="lj-stage-modal-body">
          {body}
        </div>
      </div>
    </div>
  );
}

function StatusStepper({ status, onStepClick }) {
  const active = stepIndex(status);
  const visibleSteps = status === "delivered"
    ? STATUS_STEPS.filter((s) => s.key !== "lost")
    : STATUS_STEPS;
  return (
    <div className="lj-stepper">
      {visibleSteps.map((step, i) => {
        const done = i < active;
        const current = i === active;
        return (
          <React.Fragment key={step.key}>
            <button
              type="button"
              className={`lj-step${done ? " lj-step-done" : ""}${current ? " lj-step-active" : ""}${step.key === "lost" && current ? " lj-step-lost" : ""}`}
              onClick={() => onStepClick?.(step)}
              aria-label={`Open ${step.label} stage details`}
            >
              <div className="lj-step-circle">
                <span>{i + 1}</span>
              </div>
              <span className="lj-step-label">{step.label}</span>
            </button>
            {i < visibleSteps.length - 1 && (
              <div className={`lj-step-connector${(done || current) ? " lj-connector-done" : ""}`} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

// ── Disposition timeline (horizontal) ─────────────────────────
function DispositionTimeline({ callLogs }) {
  // Build ordered unique disposition sequence from call logs (chronological)
  const sorted = [...callLogs].sort((a, b) => new Date(a.started_at) - new Date(b.started_at));
  const seq = [];
  sorted.forEach((c) => {
    const dispoLabel = c.disposition_name || c.provider_disposition;
    if (dispoLabel) {
      const last = seq[seq.length - 1];
      if (!last || last.name !== dispoLabel) {
        seq.push({ name: dispoLabel, date: c.started_at, agent: c.agent_name, fromProvider: !c.disposition_name && !!c.provider_disposition });
      }
    }
  });

  if (!seq.length) {
    return (
      <div className="lj-disp-timeline-empty">No disposition recorded yet.</div>
    );
  }

  return (
    <div className="lj-disp-timeline">
      {seq.map((item, i) => (
        <React.Fragment key={i}>
          <div className={`lj-disp-node${i === seq.length - 1 ? " lj-disp-node-current" : ""}`}>
            <div className="lj-disp-dot" />
            <span className="lj-disp-name">{item.name}</span>
            {item.fromProvider && <span className="lj-disp-provider-tag">Ozonetel</span>}
            <span className="lj-disp-date">{formatDateCell(item.date)}</span>
          </div>
          {i < seq.length - 1 && <div className="lj-disp-arrow">→</div>}
        </React.Fragment>
      ))}
    </div>
  );
}

function fmtTime(sec) {
  if (!sec || isNaN(sec) || !isFinite(sec)) return "0:00";
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
}

function CallRecordingPlayer({ call, leadId }) {
  const [playing, setPlaying] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState(false);
  const [currentTime, setCurrentTime] = React.useState(0);
  const [duration, setDuration] = React.useState(call.duration_seconds || 0);
  const [blob, setBlob] = React.useState(null);
  const [waveWidth, setWaveWidth] = React.useState(600);
  const audioRef = React.useRef(null);
  const waveWrapRef = React.useRef(null);

  const proxyUrl = `/api/crm/leads/${leadId}/call-log/${call.id}/recording/`;

  // Fetch blob for waveform
  React.useEffect(() => {
    let cancelled = false;
    fetch(proxyUrl)
      .then((r) => r.blob())
      .then((b) => { if (!cancelled) setBlob(b); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [proxyUrl]);

  // Measure container so canvas renders at native pixel width — no blur
  React.useEffect(() => {
    const el = waveWrapRef.current;
    if (!el) return;
    const obs = new ResizeObserver(([entry]) => {
      const w = Math.round(entry.contentRect.width);
      if (w > 0) setWaveWidth(w);
    });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  function toggle() {
    const el = audioRef.current;
    if (!el || error) return;
    if (playing) {
      el.pause();
    } else {
      setLoading(true);
      el.play().then(() => setLoading(false)).catch(() => {
        setLoading(false);
        setError(true);
      });
    }
  }

  function handleWaveClick(e) {
    const el = audioRef.current;
    if (!el || !duration || error) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const pct = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    el.currentTime = pct * duration;
    setCurrentTime(pct * duration);
  }

  return (
    <div className="lj-rec-player">
      <button
        type="button"
        className={`lj-play-btn${error ? " is-error" : ""}`}
        onClick={toggle}
        disabled={error}
        aria-label={error ? "Recording unavailable" : playing ? "Pause" : "Play"}
      >
        {loading ? (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="14" height="14" className="lj-spin" strokeLinecap="round">
            <circle cx="12" cy="12" r="9" strokeOpacity="0.3" />
            <path d="M12 3a9 9 0 019 9" />
          </svg>
        ) : error ? (
          <svg viewBox="0 0 20 20" fill="currentColor" width="14" height="14">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
        ) : playing ? (
          <svg viewBox="0 0 20 20" fill="currentColor" width="14" height="14">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zM7 8a1 1 0 012 0v4a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v4a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
        ) : (
          <svg viewBox="0 0 20 20" fill="currentColor" width="14" height="14">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
          </svg>
        )}
      </button>

      <div ref={waveWrapRef} className="lj-rec-wave-wrap" onClick={handleWaveClick} style={{ cursor: error ? "default" : "pointer" }}>
        {blob ? (
          <AudioVisualizer
            blob={blob}
            width={waveWidth}
            height={40}
            barWidth={2}
            gap={1}
            barColor="#c8d6f8"
            barPlayedColor="#5c85ff"
            currentTime={currentTime}
            style={{ width: "100%", height: "40px", display: "block" }}
          />
        ) : (
          <div className="lj-rec-wave-placeholder" />
        )}
      </div>

      <span className="lj-rec-time">
        {fmtTime(currentTime)}<span className="lj-rec-sep">/</span>{fmtTime(duration)}
      </span>

      <a href={proxyUrl} download className="lj-rec-dl" aria-label="Download recording" title="Download recording">
        <svg viewBox="0 0 20 20" fill="currentColor" width="14" height="14">
          <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
        </svg>
      </a>
      {error && <span className="lj-rec-error">Stream unavailable</span>}

      <audio
        ref={audioRef}
        src={proxyUrl}
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => { setPlaying(false); setCurrentTime(0); }}
        onTimeUpdate={() => {
          const el = audioRef.current;
          if (el) setCurrentTime(el.currentTime);
        }}
        onDurationChange={() => {
          const el = audioRef.current;
          if (el && isFinite(el.duration)) setDuration(el.duration);
        }}
        onError={() => { setError(true); setLoading(false); setPlaying(false); }}
        preload="none"
      />
    </div>
  );
}

function formatDur(sec) {
  if (!sec) return null;
  const m = Math.floor(sec / 60), s = sec % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}

function QualityBadge({ level }) {
  if (!level) return null;
  const l = level.toLowerCase();
  const cls = l === "good" ? "lj-q-good" : l === "poor" ? "lj-q-poor" : "lj-q-avg";
  return <span className={`lj-q-badge ${cls}`}>{startCase(level)}</span>;
}

function CallCard({ call, leadId }) {
  const dispoLabel = call.disposition_name || call.provider_disposition;
  return (
    <div className="lj-call-card">
      <div className="lj-call-card-top">
        <div className="lj-call-card-left">
          <span className="lj-call-agent">{call.agent_name || "Agent"}</span>
          <span className="lj-call-date">{formatDateCell(call.started_at)}</span>
        </div>
        <div className="lj-call-card-right">
          {call.duration_seconds ? (
            <span className="lj-call-dur">
              <svg width="11" height="11" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
              </svg>
              {formatDur(call.duration_seconds)}
            </span>
          ) : null}
          {dispoLabel && (
            <span className={`lj-call-disp${!call.disposition_name && call.provider_disposition ? " lj-call-disp-provider" : ""}`}>
              {dispoLabel}
              {!call.disposition_name && call.provider_disposition && (
                <span className="lj-call-disp-src">Ozonetel</span>
              )}
            </span>
          )}
        </div>
      </div>
      {call.remarks && <p className="lj-call-remarks">{call.remarks}</p>}
      {call.recording_url && <CallRecordingPlayer call={call} leadId={leadId} />}
    </div>
  );
}


const PHASE1_TYPES = new Set(["call", "status_change", "disposition_change", "transfer", "note", "system", "duplicate"]);

function activityIcon(type) {
  switch (type) {
    case "call": return { emoji: "📞", cls: "lj-act-call" };
    case "status_change": return { emoji: "🔄", cls: "lj-act-status" };
    case "disposition_change": return { emoji: "✅", cls: "lj-act-disp" };
    case "transfer": return { emoji: "🔀", cls: "lj-act-transfer" };
    case "note": return { emoji: "📝", cls: "lj-act-note" };
    case "system": return { emoji: "🚗", cls: "lj-act-system" };
    case "duplicate": return { emoji: "⚠️", cls: "lj-act-note" };
    case "__created__": return { emoji: "🌱", cls: "lj-act-created" };
    default: return { emoji: "•", cls: "lj-act-note" };
  }
}

function activityLabel(item) {
  switch (item.activity_type) {
    case "call": return "Call attempt";
    case "status_change": return item.title || "Status updated";
    case "disposition_change": return item.title || "Disposition updated";
    case "transfer": return item.title || "Lead transferred";
    case "note": return item.title || "Note added";
    case "system": return item.title || "Test drive update";
    case "duplicate": return item.title || "Duplicate submission";
    case "__created__": return "Lead created";
    default: return item.title || startCase(item.activity_type);
  }
}

function ActivityTimeline({ activities, lead }) {
  // Synthetic "created" event
  const createdEvent = {
    id: "__created__",
    activity_type: "__created__",
    occurred_at: lead.registered_at || lead.created_at,
    actor_name: lead.assigned_agent?.full_name || null,
    description: `Source: ${lead.source_name || "—"}`,
  };

  const filtered = activities.filter((a) => PHASE1_TYPES.has(a.activity_type));
  // Sort ascending (oldest first) so timeline reads top-down
  const sorted = [...filtered].sort((a, b) => new Date(a.occurred_at) - new Date(b.occurred_at));
  const items = [createdEvent, ...sorted];

  return (
    <div className="lj-act-timeline">
      {items.map((item, idx) => {
        const { emoji, cls } = activityIcon(item.activity_type);
        const isLast = idx === items.length - 1;
        return (
          <div className="lj-act-row" key={item.id}>
            <div className="lj-act-spine">
              <div className={`lj-act-dot ${cls}`}>{emoji}</div>
              {!isLast && <div className="lj-act-line" />}
            </div>
            <div className="lj-act-content">
              <div className="lj-act-topline">
                <span className="lj-act-label">{activityLabel(item)}</span>
                <span className="lj-act-time">{formatDateCell(item.occurred_at)}</span>
              </div>
              {item.description && (
                <p className="lj-act-desc">{item.description}</p>
              )}
              {item.actor_name && (
                <p className="lj-act-agent">{item.actor_name}</p>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── Tags section ──────────────────────────────────────────────
function TagsSection({ tags, onUpdateTags, leadId }) {
  const [input, setInput] = React.useState("");
  const [busy, setBusy] = React.useState(false);

  async function add() {
    const t = input.trim();
    if (!t || tags.includes(t)) { setInput(""); return; }
    setBusy(true);
    await onUpdateTags(leadId, [...tags, t]);
    setInput("");
    setBusy(false);
  }

  async function remove(t) {
    setBusy(true);
    await onUpdateTags(leadId, tags.filter((x) => x !== t));
    setBusy(false);
  }

  return (
    <div className="lj-tags-body">
      <div className="lj-tag-chips">
        {tags.length
          ? tags.map((t) => (
            <span key={t} className="lj-tag-chip">
              {t}
              <button type="button" onClick={() => remove(t)} disabled={busy}>×</button>
            </span>
          ))
          : <span className="lj-empty-inline">No tags.</span>}
      </div>
      <div className="lj-tag-input-row">
        <input className="lj-tag-input" placeholder="Add tag…" value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), add())} disabled={busy} />
        <button type="button" className="lj-tag-add-btn" onClick={add} disabled={busy || !input.trim()}>Add</button>
      </div>
    </div>
  );
}

// ── Collapsible section wrapper ───────────────────────────────
function Accordion({ icon: Icon, title, badge, defaultOpen = true, children }) {
  const [open, setOpen] = React.useState(defaultOpen);
  return (
    <div className="lj-accordion">
      <button type="button" className="lj-accordion-head" onClick={() => setOpen((o) => !o)}>
        <span className="lj-accordion-icon"><Icon /></span>
        <span className="lj-accordion-title">{title}</span>
        {badge != null && <span className="lj-count">{badge}</span>}
        <span className="lj-accordion-chevron">{open ? <ChevronUpIcon /> : <ChevronDownIcon />}</span>
      </button>
      {open && <div className="lj-accordion-body">{children}</div>}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// Main component
// ══════════════════════════════════════════════════════════════
function LeadJourneyModal({
  agents,
  dispositionGroups,
  documentFeedback,
  documentUploadLoading,
  fetchJson,
  isAdmin,
  lead,
  loading,
  onClose,
  onCreateFollowUp,
  onLogCall,
  onSaveDisposition,
  onUpdateTags,
  onUploadDocuments,
  open,
  roleScope,
}) {
  const [docType, setDocType] = React.useState("additional_document");
  const [localDocErr, setLocalDocErr] = React.useState("");
  const [selectedFiles, setSelectedFiles] = React.useState([]);
  const [showAllCalls, setShowAllCalls] = React.useState(false);
  const [fuTitle, setFuTitle] = React.useState("");
  const [fuDatetime, setFuDatetime] = React.useState("");
  const [fuNotes, setFuNotes] = React.useState("");
  const [fuBusy, setFuBusy] = React.useState(false);
  const [fuErr, setFuErr] = React.useState("");
  const [editDispoOpen, setEditDispoOpen] = React.useState(false);
  const [, setTick] = React.useState(0);
  const [inlineDispositionForm, setInlineDispositionForm] = React.useState({
    agentId: "",
    dispositionId: "",
    nextFollowUp: "",
    remarks: "",
    subDispositionId: "",
  });
  const [inlineDispoBusy, setInlineDispoBusy] = React.useState(false);
  const [inlineDispoErr, setInlineDispoErr] = React.useState("");
  const [inlineSatisfiedChoice, setInlineSatisfiedChoice] = React.useState(null); // null | true | false
  const [invData, setInvData] = React.useState(null);
  const [invError, setInvError] = React.useState("");
  const [invFilterCity, setInvFilterCity] = React.useState("");
  const [invFilterOem, setInvFilterOem] = React.useState("");
  const [invFilterDealer, setInvFilterDealer] = React.useState("");
  const [selectedStep, setSelectedStep] = React.useState(null);

  React.useEffect(() => {
    if (!open) return undefined;
    const timer = setInterval(() => setTick((n) => n + 1), 30000);
    return () => clearInterval(timer);
  }, [open]);

  React.useEffect(() => {
    if (!open) {
      setDocType("additional_document");
      setLocalDocErr("");
      setSelectedFiles([]);
      setShowAllCalls(false);
      setFuTitle(""); setFuDatetime(""); setFuNotes(""); setFuErr("");
      setEditDispoOpen(false);
      setInlineDispositionForm({
        agentId: "",
        dispositionId: "",
        nextFollowUp: "",
        remarks: "",
        subDispositionId: "",
      });
      setInlineDispoErr("");
      setSelectedStep(null);
    }
  }, [lead?.id, open]);

  // ── Disposition-derived state (must be before early returns) ──
  const inlineRootDispositions = (dispositionGroups || [])
    .map((group) => group.dispositions?.[0])
    .filter(Boolean);
  const inlineSelectedRootDisposition = inlineRootDispositions.find(
    (item) => String(item.id) === String(inlineDispositionForm.dispositionId),
  );
  const inlineSubDispoOptions = inlineSelectedRootDisposition?.children || [];
  const selectedInlineSubDisposition = inlineSubDispoOptions.find(
    (item) => String(item.id) === String(inlineDispositionForm.subDispositionId),
  );
  const inlineFollowUpDisposition = selectedInlineSubDisposition || inlineSelectedRootDisposition;
  const shouldShowInlineSubDisposition = Boolean(
    inlineDispositionForm.dispositionId && inlineSubDispoOptions.length > 0,
  );
  const shouldShowInlineNextFollowUp = Boolean(inlineFollowUpDisposition?.requires_follow_up);
  const shouldShowInlineAgentSelect = roleScope?.scope !== "own_leads";

  // Build dispositionId → group slug map for reliable detection (same as CallDispositionPopup)
  const inlineDispIdToGroupSlug = React.useMemo(() => {
    const map = {};
    (dispositionGroups || []).forEach((g) => {
      const addAll = (disps) => disps.forEach((d) => {
        map[String(d.id)] = g.slug || "";
        if (d.children) addAll(d.children);
      });
      addAll(g.dispositions || []);
    });
    return map;
  }, [dispositionGroups]);

  const inlineActiveDispId = inlineDispositionForm.subDispositionId || inlineDispositionForm.dispositionId;
  const inlineActiveGroupSlug = inlineDispIdToGroupSlug[String(inlineActiveDispId)] || "";

  const inlineIsTestDriveConducted = inlineActiveGroupSlug === "test-drive-conducted" || Boolean(
    (inlineSelectedRootDisposition?.name || "").toLowerCase().includes("test drive conducted") ||
    (selectedInlineSubDisposition?.name  || "").toLowerCase().includes("test drive conducted"),
  );
  const inlineIsTestDrive = !inlineIsTestDriveConducted && (
    inlineActiveGroupSlug === "interested-for-test-drive" || Boolean(
      (inlineSelectedRootDisposition?.name || "").toLowerCase().includes("test drive") ||
      (selectedInlineSubDisposition?.name  || "").toLowerCase().includes("test drive"),
    )
  );
  // Show inventory for scheduling (or rescheduling when not satisfied after conducted)
  const inlineShowInventory = inlineIsTestDrive || (inlineIsTestDriveConducted && inlineSatisfiedChoice === false);

  // Reset satisfaction when disposition changes
  React.useEffect(() => {
    setInlineSatisfiedChoice(null);
  }, [inlineDispositionForm.dispositionId, inlineDispositionForm.subDispositionId]);

  React.useEffect(() => {
    if (!open || !(inlineIsTestDrive || inlineIsTestDriveConducted) || !fetchJson) return;
    let active = true;
    setInvData(null);
    setInvError("");
    fetchJson("/api/crm/inventory/", { cache: "no-store" })
      .then((data) => { if (active) setInvData(data); })
      .catch((error) => { if (active) setInvError(error.message || "Could not load inventory."); });
    return () => { active = false; };
  }, [open, inlineIsTestDrive, inlineIsTestDriveConducted, fetchJson]);

  React.useEffect(() => {
    if (!inlineIsTestDrive && !inlineIsTestDriveConducted) {
      setInvFilterCity("");
      setInvFilterOem("");
      setInvFilterDealer("");
    }
  }, [inlineIsTestDrive, inlineIsTestDriveConducted]);

  const invRows = invData?.rows || [];
  // Fully bidirectional — each field's options reflect the other two selections.
  const invCityOptions = React.useMemo(() => (
    [...new Set(
      invRows
        .filter((r) => inventoryMatches(r, { oem: invFilterOem, dealer: invFilterDealer }))
        .map((r) => r.city).filter(Boolean)
    )].sort()
  ), [invRows, invFilterOem, invFilterDealer]);
  const invOemOptions = React.useMemo(() => (
    [...new Set(
      invRows
        .filter((r) => inventoryMatches(r, { city: invFilterCity, dealer: invFilterDealer }))
        .map((r) => r.oem).filter(Boolean)
    )].sort()
  ), [invRows, invFilterCity, invFilterDealer]);
  const invDealerOptions = React.useMemo(() => (
    [...new Set(
      invRows
        .filter((r) => inventoryMatches(r, { city: invFilterCity, oem: invFilterOem }))
        .map((r) => r.dealer).filter(Boolean)
    )].sort()
  ), [invRows, invFilterCity, invFilterOem]);

  function clearInlineInventory() {
    setInvFilterCity("");
    setInvFilterOem("");
    setInvFilterDealer("");
  }

  const [waSending, setWaSending] = React.useState(false);
  const [waMsg, setWaMsg] = React.useState("");
  const [waPreview, setWaPreview] = React.useState(null);
  const [waLoadingPreview, setWaLoadingPreview] = React.useState(false);
  async function openWhatsAppPreview() {
    if (!fetchJson || !lead?.id) return;
    setWaLoadingPreview(true);
    setWaMsg("");
    try {
      const data = await fetchJson(`/api/crm/leads/${lead.id}/send-td-confirmation/`);
      setWaPreview(data);
    } catch (e) {
      setWaMsg(e.message || "Could not load preview.");
    } finally {
      setWaLoadingPreview(false);
    }
  }
  async function confirmSendWhatsApp() {
    if (!fetchJson || !lead?.id) return;
    setWaSending(true);
    try {
      await fetchJson(`/api/crm/leads/${lead.id}/send-td-confirmation/`, { method: "POST", body: "{}" });
      setWaPreview(null);
      setWaMsg("✓ WhatsApp confirmation sent to customer.");
    } catch (e) {
      setWaMsg(e.message || "Failed to send WhatsApp.");
    } finally {
      setWaSending(false);
    }
  }
  const invUnits = React.useMemo(
    () => availableInventoryUnits(invRows, {
      city: invFilterCity, oem: invFilterOem, dealer: invFilterDealer,
    }),
    [invRows, invFilterCity, invFilterDealer, invFilterOem],
  );

  if (!open) return null;

  if (loading && !lead) {
    return (
      <div className="modal-backdrop" onClick={onClose}>
        <div className="modal-panel large lj-panel" onClick={(e) => e.stopPropagation()}>
          <div className="lj-breadcrumb-bar">
            <span className="lj-breadcrumb">Leads</span>
            <button type="button" className="close-button" onClick={onClose}><CloseIcon /></button>
          </div>
          <div className="journey-loading-state">Loading lead journey…</div>
        </div>
      </div>
    );
  }

  if (!lead) return null;

  const canCall = Boolean(roleScope?.can_call);
  const recentCallForDisposition = lead.call_logs?.[0] || null;

  function buildInlineDispositionForm() {
    return {
      agentId: String(recentCallForDisposition?.agent || lead.assigned_to || ""),
      dispositionId: String(lead.current_disposition || ""),
      nextFollowUp:
        toInputDateTime(recentCallForDisposition?.next_follow_up_at) ||
        toInputDateTime(lead.next_follow_up_at) ||
        "",
      remarks: recentCallForDisposition?.remarks || "",
      subDispositionId: String(lead.current_sub_disposition || ""),
    };
  }

  function handleInlineDispositionChange(field, value, resetChild = false) {
    setInlineDispositionForm((current) => {
      const next = {
        ...current,
        [field]: value,
        ...(resetChild ? { subDispositionId: "" } : {}),
      };

      if (field === "dispositionId") {
        const nextRoot = inlineRootDispositions.find((item) => String(item.id) === String(value));
        if (!nextRoot?.requires_follow_up) {
          next.nextFollowUp = "";
        }
      }

      if (field === "subDispositionId" && value) {
        const nextSub = inlineSubDispoOptions.find((item) => String(item.id) === String(value));
        if (!nextSub?.requires_follow_up) {
          next.nextFollowUp = "";
        }
      }

      return next;
    });
  }

  async function handleInlineDispoSave(e) {
    e.preventDefault();
    if (!inlineDispositionForm.dispositionId) {
      setInlineDispoErr("Please select a disposition.");
      return;
    }
    if (shouldShowInlineAgentSelect && !inlineDispositionForm.agentId) {
      setInlineDispoErr("Please select an agent.");
      return;
    }
    if (inlineIsTestDriveConducted && inlineSatisfiedChoice === null) {
      setInlineDispoErr("Please select whether the customer was satisfied.");
      return;
    }
    if ((shouldShowInlineNextFollowUp || inlineShowInventory) && !inlineDispositionForm.nextFollowUp) {
      setInlineDispoErr(inlineShowInventory ? "Please select a schedule date." : "Please select the next follow-up date.");
      return;
    }
    if (inlineShowInventory && !invFilterCity) {
      setInlineDispoErr("Please select a City.");
      return;
    }
    if (inlineShowInventory && !invFilterOem) {
      setInlineDispoErr("Please select an OEM.");
      return;
    }
    if (inlineShowInventory && !invFilterDealer) {
      setInlineDispoErr("Please select a Dealer.");
      return;
    }
    if (inlineShowInventory && invUnits === 0) {
      setInlineDispoErr("No inventory available for the selected OEM / Dealer. Please choose another.");
      return;
    }
    setInlineDispoBusy(true); setInlineDispoErr("");
    try {
      const ok = await onSaveDisposition(lead.id, {
        ...inlineDispositionForm,
        nextFollowUp:        (shouldShowInlineNextFollowUp || inlineShowInventory) ? inlineDispositionForm.nextFollowUp : "",
        scheduleCity:        inlineShowInventory ? invFilterCity   : "",
        scheduleOem:         inlineShowInventory ? invFilterOem    : "",
        scheduleDealer:      inlineShowInventory ? invFilterDealer : "",
        testDriveConducted:  inlineIsTestDriveConducted,
        satisfied:           inlineIsTestDriveConducted ? inlineSatisfiedChoice : null,
      });
      if (ok) {
        setEditDispoOpen(false);
        setInlineDispositionForm(buildInlineDispositionForm());
      } else {
        setInlineDispoErr("Could not save. Try again.");
      }
    } catch (err) {
      setInlineDispoErr(err.message || "Could not save. Try again.");
    }
    setInlineDispoBusy(false);
  }

  const activities = lead.activities || [];
  const assignments = lead.assignments || [];
  const callLogs = [...(lead.call_logs || [])].sort((a, b) => new Date(b.started_at) - new Date(a.started_at));
  const documents = lead.documents || [];
  const duplicates = lead.duplicate_matches || [];
  const followUps = lead.follow_ups || [];
  const tags = lead.tags || [];
  const customFields = lead.custom_fields || {};

  const mostRecentCall = callLogs[0] || null;
  const olderCalls = callLogs.slice(1);

  // 24-hour first-call SLA
  const noFirstCall = !lead.last_contacted_at && callLogs.length === 0;
  const sla = (() => {
    if (!noFirstCall) return null;
    const created = new Date(lead.sla_clock_started_at || lead.registered_at || lead.created_at).getTime();
    if (Number.isNaN(created)) return null;
    const deadlineMs = created + 24 * 60 * 60 * 1000;
    const remainingMs = deadlineMs - Date.now();
    const breached = remainingMs <= 0;
    const totalMinutes = Math.abs(Math.floor(remainingMs / 60000));
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    const label = breached
      ? `${hours}h ${minutes}m overdue`
      : hours > 0
        ? `${hours}h ${minutes}m left`
        : `${minutes}m left`;
    return { breached, label };
  })();
  const remainingSlots = Math.max(MAX_LEAD_DOCUMENTS - documents.length, 0);
  const palette = avatarPalette(lead.full_name);
  const cfEntries = Object.entries(customFields);

  function handleDocSelection(e) {
    const files = Array.from(e.target.files || []);
    const err = validateDocumentSelection({ allowedExtensions: DOCUMENT_ALLOWED_EXTENSIONS, existingCount: documents.length, files, maxDocuments: MAX_LEAD_DOCUMENTS });
    setSelectedFiles(files);
    setLocalDocErr(err);
  }

  async function handleDocSubmit(e) {
    e.preventDefault();
    const err = validateDocumentSelection({ allowedExtensions: DOCUMENT_ALLOWED_EXTENSIONS, existingCount: documents.length, files: selectedFiles, maxDocuments: MAX_LEAD_DOCUMENTS });
    if (err) { setLocalDocErr(err); return; }
    const ok = await onUploadDocuments(selectedFiles, docType);
    if (ok) { setDocType("additional_document"); setLocalDocErr(""); setSelectedFiles([]); }
  }

  async function handleFuSubmit(e) {
    e.preventDefault();
    if (!fuDatetime) { setFuErr("Pick a date and time."); return; }
    setFuBusy(true); setFuErr("");
    const ok = await onCreateFollowUp(lead.id, { title: fuTitle || "Follow-up", scheduled_for: new Date(fuDatetime).toISOString(), notes: fuNotes });
    if (ok) { setFuTitle(""); setFuDatetime(""); setFuNotes(""); }
    else { setFuErr("Could not schedule. Try again."); }
    setFuBusy(false);
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal-panel large lj-panel" onClick={(e) => e.stopPropagation()}>

        {/* ── Breadcrumb bar ──────────────────────────────── */}
        <div className="lj-breadcrumb-bar">
          <nav className="lj-breadcrumb">
            <span className="lj-bc-seg lj-bc-parent">Leads</span>
            <span className="lj-bc-sep">/</span>
            <span className="lj-bc-seg lj-bc-current">{lead.full_name}</span>
          </nav>
          <div className="lj-breadcrumb-actions">
            {canCall && onLogCall && (
              <button
                type="button"
                className="lj-action-btn lj-action-call"
                onClick={() => onLogCall(lead.id)}
                disabled={editDispoOpen}
              >
                <PhoneIcon />
                <span>Call Now</span>
              </button>
            )}
            {canCall && onSaveDisposition ? (
              <button
                type="button"
                className={`lj-action-btn ${editDispoOpen ? "lj-action-call" : "lj-action-dispo"}`}
                onClick={() => {
                  if (editDispoOpen) {
                    return;
                  }
                  setEditDispoOpen(true);
                  setInlineDispositionForm(buildInlineDispositionForm());
                  setInlineDispoErr("");
                }}
                disabled={editDispoOpen}
              >
                <span>{editDispoOpen ? "Save Disposition First" : "Edit Disposition"}</span>
              </button>
            ) : null}
            <button type="button" className="close-button" onClick={onClose}><CloseIcon /></button>
          </div>
        </div>

        {/* ── Profile strip ───────────────────────────────── */}
        <div className="lj-profile-strip">
          <div style={{ display: "flex", alignItems: "center", gap: 12, flex: 1, minWidth: 0 }}>
            <div className="lj-avatar" style={{ background: palette.bg, color: palette.fg }}>
              {initials(lead.full_name)}
            </div>
            <div className="lj-profile-info">
              <div className="lj-profile-name-row">
                <span className="lj-name">{lead.full_name}</span>
                {lead.source_name && <span className="lj-source-tag">{lead.source_name}</span>}
              </div>
              <div className="lj-profile-meta">
                {lead.phone_number && <span><PhoneIcon />{displayPhone(lead, roleScope)}</span>}
                {lead.email && <span>{lead.email}</span>}
                {lead.client_id && <span>ID: {lead.client_id}</span>}
                {lead.city_name && <span>{lead.city_name}</span>}
                {lead.assigned_agent?.full_name && <span><UserGroupIcon />{lead.assigned_agent.full_name}</span>}
              </div>
            </div>
          </div>
        </div>

        {/* ── 24-hour first-call SLA banner ───────────────── */}
        {sla && (
          <div className={`lj-sla-banner${sla.breached ? " lj-sla-banner-alert" : " lj-sla-banner-warn"}`}>
            <span className="lj-sla-icon" aria-hidden="true">{sla.breached ? "🔴" : "⏳"}</span>
            <span className="lj-sla-text">
              {sla.breached
                ? <><strong>First call overdue</strong> — no call made yet. SLA breached by {sla.label}.</>
                : <><strong>First call SLA:</strong> {sla.label} to make the first call.</>}
            </span>
            {sla.breached && canCall && onLogCall && (
              <button
                type="button"
                className="lj-sla-call-btn"
                onClick={() => onLogCall(lead.id)}
              >
                <PhoneIcon /> Call Now
              </button>
            )}
          </div>
        )}

        {/* ── Status stepper ──────────────────────────────── */}
        <div className="lj-stepper-wrap">
          <StatusStepper status={lead.status} onStepClick={setSelectedStep} />
        </div>

        {/* ── Inline edit disposition ─────────────────────── */}
        {editDispoOpen && (
          <div style={{ padding: "12px 20px", borderBottom: "1px solid var(--line)", background: "var(--surface-soft)", overflowY: "auto", flex: 1, minHeight: 0 }}>
            <form className="call-panel-form" onSubmit={handleInlineDispoSave}>
              <div className="form-grid compact">
                {shouldShowInlineAgentSelect ? (
                  <label className="field-stack">
                    <span>
                      Agent
                      {" "}
                      <span style={{ color: "var(--red)" }}>*</span>
                    </span>
                    <select
                      value={inlineDispositionForm.agentId}
                      onChange={(e) => handleInlineDispositionChange("agentId", e.target.value)}
                      required
                    >
                      <option value="">Select agent</option>
                      {(agents || []).map((agent) => (
                        <option key={agent.id} value={agent.id}>{agent.full_name}</option>
                      ))}
                    </select>
                  </label>
                ) : null}

                <label className="field-stack">
                  <span>Disposition</span>
                  <select
                    value={inlineDispositionForm.dispositionId}
                    onChange={(e) => handleInlineDispositionChange("dispositionId", e.target.value, true)}
                  >
                    <option value="">Select disposition</option>
                    {(dispositionGroups || []).map((group) => {
                      const root = group.dispositions?.[0];
                      if (!root) return null;
                      return <option key={root.id} value={root.id}>{group.name}</option>;
                    })}
                  </select>
                </label>

                {shouldShowInlineSubDisposition ? (
                  <label className="field-stack">
                    <span>Sub-Disposition</span>
                    <select
                      value={inlineDispositionForm.subDispositionId}
                      onChange={(e) => handleInlineDispositionChange("subDispositionId", e.target.value)}
                    >
                      <option value="">Select sub-disposition</option>
                      {inlineSubDispoOptions.map((item) => (
                        <option key={item.id} value={item.id}>{item.name}</option>
                      ))}
                    </select>
                  </label>
                ) : null}

                {/* Satisfied Y/N for Test Drive Conducted */}
                {inlineIsTestDriveConducted && (
                  <div className="field-stack full-span">
                    <span style={{ fontSize: 12, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase", letterSpacing: "0.06em" }}>
                      Customer Satisfied? <span style={{ color: "var(--red)" }}>*</span>
                    </span>
                    <div style={{ display: "flex", gap: 10, marginTop: 6 }}>
                      <button
                        type="button"
                        onClick={() => setInlineSatisfiedChoice(true)}
                        style={{
                          flex: 1, padding: "10px 0", borderRadius: 8, fontWeight: 700, fontSize: 13, cursor: "pointer", border: "1.5px solid",
                          background: inlineSatisfiedChoice === true ? "#dcfce7" : "#f9fafb",
                          color: inlineSatisfiedChoice === true ? "#15803d" : "#6b7280",
                          borderColor: inlineSatisfiedChoice === true ? "#86efac" : "#e5e7eb",
                        }}
                      >✅ Yes, Satisfied</button>
                      <button
                        type="button"
                        onClick={() => setInlineSatisfiedChoice(false)}
                        style={{
                          flex: 1, padding: "10px 0", borderRadius: 8, fontWeight: 700, fontSize: 13, cursor: "pointer", border: "1.5px solid",
                          background: inlineSatisfiedChoice === false ? "#fee2e2" : "#f9fafb",
                          color: inlineSatisfiedChoice === false ? "#b91c1c" : "#6b7280",
                          borderColor: inlineSatisfiedChoice === false ? "#fca5a5" : "#e5e7eb",
                        }}
                      >❌ No, Reschedule</button>
                    </div>
                    {inlineSatisfiedChoice === false && (
                      <p style={{ fontSize: 12, color: "#b45309", margin: "6px 0 0", fontWeight: 600 }}>
                        Schedule a new test drive for the customer below.
                      </p>
                    )}
                  </div>
                )}

                {(shouldShowInlineNextFollowUp || inlineShowInventory) ? (
                  <label className="field-stack">
                    <span>
                      {inlineShowInventory ? (inlineIsTestDriveConducted ? "New Schedule Date" : "Schedule Date") : shouldShowInlineSubDisposition ? "Next Follow-up" : "Date"}
                      {" "}
                      <span style={{ color: "var(--red)" }}>*</span>
                    </span>
                    <input
                      type="datetime-local"
                      min={isAdmin ? undefined : new Date().toISOString().slice(0, 16)}
                      value={inlineDispositionForm.nextFollowUp}
                      onChange={(e) => handleInlineDispositionChange("nextFollowUp", e.target.value)}
                      required
                    />
                  </label>
                ) : null}

                {inlineShowInventory && (
                  <div className="field-stack full-span">
                    <div style={{
                      border: "1.5px solid var(--line)", borderRadius: 10,
                      overflow: "hidden", background: "var(--surface-soft)",
                    }}>
                      <div style={{
                        padding: "7px 12px", background: "var(--blue)", color: "#fff",
                        fontSize: 11, fontWeight: 700, letterSpacing: "0.07em", textTransform: "uppercase",
                        display: "flex", alignItems: "center", justifyContent: "space-between",
                      }}>
                        <span>Inventory</span>
                        {(invFilterCity || invFilterOem || invFilterDealer) && (
                          <button
                            type="button"
                            onClick={clearInlineInventory}
                            style={{ background: "rgba(255,255,255,0.2)", border: "none", color: "#fff", fontSize: 11, fontWeight: 700, padding: "2px 10px", borderRadius: 6, cursor: "pointer", letterSpacing: 0, textTransform: "none" }}
                          >
                            Clear
                          </button>
                        )}
                      </div>
                      <div style={{ padding: "10px 12px" }}>
                        {!invData ? (
                          <p style={{ fontSize: 12, color: invError ? "var(--red)" : "var(--muted)", margin: 0 }}>{invError || "Loading inventory…"}</p>
                        ) : (
                          <>
                            <label className="field-stack" style={{ margin: "0 0 10px" }}>
                              <span>City <span style={{ color: "var(--red)" }}>*</span></span>
                              <SearchableSelect
                                value={invFilterCity}
                                onChange={setInvFilterCity}
                                options={invCityOptions}
                                placeholder="Select City"
                              />
                            </label>
                            <label className="field-stack" style={{ margin: "0 0 10px" }}>
                              <span>OEM <span style={{ color: "var(--red)" }}>*</span></span>
                              <select
                                value={invFilterOem}
                                onChange={(e) => setInvFilterOem(e.target.value)}
                              >
                                <option value="">Select OEM</option>
                                {invOemOptions.map((v) => <option key={v} value={v}>{v}</option>)}
                              </select>
                            </label>
                            <label className="field-stack" style={{ margin: "0 0 10px" }}>
                              <span>Dealer <span style={{ color: "var(--red)" }}>*</span></span>
                              <select
                                value={invFilterDealer}
                                onChange={(e) => setInvFilterDealer(e.target.value)}
                              >
                                <option value="">Select Dealer</option>
                                {invDealerOptions.map((v) => <option key={v} value={v}>{v}</option>)}
                              </select>
                            </label>
                            <div style={{
                              display: "flex", alignItems: "center", justifyContent: "space-between",
                              padding: "8px 12px", borderRadius: 8,
                              background: "#fff", border: `1.5px solid ${invUnits === 0 ? "var(--red)" : "var(--line)"}`,
                            }}>
                              <span style={{ fontSize: 13, fontWeight: 600, color: "var(--muted)" }}>Available Units</span>
                              <span style={{ fontSize: 22, fontWeight: 800, color: invUnits === 0 ? "var(--red)" : "var(--blue)" }}>{invUnits}</span>
                            </div>

                            {/* Send WhatsApp confirmation for the existing scheduled test drive. */}
                            <button
                              type="button"
                              onClick={openWhatsAppPreview}
                              disabled={waLoadingPreview}
                              style={{
                                marginTop: 10, width: "100%", display: "flex", alignItems: "center",
                                justifyContent: "center", gap: 8, padding: "9px 12px", borderRadius: 8,
                                border: "none", cursor: waSending ? "default" : "pointer",
                                fontWeight: 700, fontSize: 13, color: "#fff",
                                background: "linear-gradient(135deg,#25D366,#1da851)",
                                opacity: waLoadingPreview ? 0.6 : 1,
                              }}
                            >
                              {waLoadingPreview ? "Loading…" : (<><WhatsAppIcon /> Send WhatsApp Confirmation</>)}
                            </button>
                            {waMsg && (
                              <p style={{ margin: "6px 0 0", fontSize: 12, fontWeight: 600, color: waMsg.startsWith("✓") ? "#15803d" : "var(--red)" }}>
                                {waMsg}
                              </p>
                            )}
                            {waPreview && (
                              <div
                                onClick={() => !waSending && setWaPreview(null)}
                                style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 10000, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}
                              >
                                <div onClick={(e) => e.stopPropagation()} style={{ background: "#fff", borderRadius: 14, width: "100%", maxWidth: 380, overflow: "hidden", boxShadow: "0 12px 40px rgba(0,0,0,0.3)" }}>
                                  <div style={{ background: "linear-gradient(135deg,#25D366,#1da851)", color: "#fff", padding: "12px 16px", fontWeight: 700, fontSize: 14 }}>💬 WhatsApp Preview</div>
                                  <div style={{ padding: 16 }}>
                                    <p style={{ margin: "0 0 8px", fontSize: 12, color: "#6b7280" }}>To: <strong style={{ color: "#1a2b4a" }}>+{waPreview.phone || "—"}</strong></p>
                                    <div style={{ background: "#dcf8c6", borderRadius: 10, padding: "10px 12px", fontSize: 13, lineHeight: 1.5, color: "#1a2b4a", whiteSpace: "pre-wrap", border: "1px solid #c5ead0" }}>{waPreview.message}</div>
                                    <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
                                      <button type="button" onClick={() => setWaPreview(null)} disabled={waSending} style={{ flex: 1, padding: "9px 0", borderRadius: 8, border: "1.5px solid #d1d5db", background: "#fff", color: "#374151", fontWeight: 600, fontSize: 13, cursor: "pointer" }}>Cancel</button>
                                      <button type="button" onClick={confirmSendWhatsApp} disabled={waSending} style={{ flex: 2, padding: "9px 0", borderRadius: 8, border: "none", background: "linear-gradient(135deg,#25D366,#1da851)", color: "#fff", fontWeight: 700, fontSize: 13, cursor: waSending ? "default" : "pointer", opacity: waSending ? 0.6 : 1 }}>{waSending ? "Sending…" : "Send Now"}</button>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                <label className="field-stack full-span">
                  <span>Remarks</span>
                  <textarea
                    rows="2"
                    value={inlineDispositionForm.remarks}
                    onChange={(e) => handleInlineDispositionChange("remarks", e.target.value)}
                    placeholder="Call summary, objections, commitments..."
                  />
                </label>

                {shouldShowInlineNextFollowUp ? (
                  <p className="helper-text full-span">
                    This disposition requires a next follow-up date.
                  </p>
                ) : null}

                {inlineDispoErr ? <p className="lj-err full-span">{inlineDispoErr}</p> : null}
              </div>
              <div className="call-panel-submit">
                <button type="submit" className="primary-button modal-submit" disabled={inlineDispoBusy}>
                  {inlineDispoBusy ? "Saving..." : "Save Disposition"}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* ── Single scrollable body ───────────────────────── */}
        <div
          className={`lj-scroll-body${editDispoOpen ? " lj-scroll-body-locked" : ""}`}
          aria-hidden={editDispoOpen}
        >

          {/* DISPOSITION TIMELINE */}
          <div className="lj-section">
            <div className="lj-section-title">Disposition Progression</div>
            <DispositionTimeline callLogs={lead.call_logs || []} />
          </div>

          {/* CALL RECORDINGS */}
          <div className="lj-section">
            <div className="lj-section-title">
              Call Recordings
              {callLogs.length > 0 && <span className="lj-count">{callLogs.length}</span>}
            </div>
            {callLogs.length === 0 && (
              <p className="lj-empty">No calls logged yet.</p>
            )}
            {mostRecentCall && (
              <CallCard call={mostRecentCall} leadId={lead.id} />
            )}
            {olderCalls.length > 0 && (
              <>
                <button type="button" className="lj-show-more-btn" onClick={() => setShowAllCalls((v) => !v)}>
                  {showAllCalls ? <ChevronUpIcon /> : <ChevronDownIcon />}
                  {showAllCalls ? "Hide" : `${olderCalls.length} more call${olderCalls.length > 1 ? "s" : ""}`}
                </button>
                {showAllCalls && olderCalls.map((c) => <CallCard key={c.id} call={c} leadId={lead.id} />)}
              </>
            )}
          </div>

          {/* ACTIVITY TIMELINE */}
          <div className="lj-section">
            <div className="lj-section-title">Activity Timeline</div>
            <ActivityTimeline activities={activities} lead={lead} />
          </div>

          {/* FOLLOW-UPS */}
          <Accordion icon={CalendarIcon} title="Follow-ups" badge={followUps.length || null} defaultOpen={followUps.length > 0}>
            <form className="lj-fu-form" onSubmit={handleFuSubmit}>
              <div className="lj-fu-fields">
                <input className="lj-fu-input" placeholder="Title (optional)" value={fuTitle} onChange={(e) => setFuTitle(e.target.value)} />
                <input type="datetime-local" className="lj-fu-input" value={fuDatetime} onChange={(e) => setFuDatetime(e.target.value)} required />
              </div>
              <textarea className="lj-fu-input lj-fu-notes" rows="2" placeholder="Notes (optional)" value={fuNotes} onChange={(e) => setFuNotes(e.target.value)} />
              {fuErr && <p className="lj-err">{fuErr}</p>}
              <button type="submit" className="primary-button modal-submit lj-fu-submit" disabled={fuBusy}>
                {fuBusy ? "Scheduling…" : "Schedule Call"}
              </button>
            </form>
            {followUps.slice(0, 5).map((fu) => (
              <div className="lj-fu-row" key={fu.id}>
                <span className={`lj-fu-badge lj-fu-${fu.status}`}>{startCase(fu.status)}</span>
                <div className="lj-fu-row-info">
                  <span className="lj-fu-row-title">{fu.title || "Follow-up"}</span>
                  <span className="lj-fu-row-date">{formatDateCell(fu.scheduled_for)}</span>
                </div>
              </div>
            ))}
          </Accordion>

          {/* DOCUMENTS */}
          <Accordion icon={DocumentIcon} title="Additional Documents" badge={`${documents.length}/${MAX_LEAD_DOCUMENTS}`} defaultOpen={false}>
            <div className="lj-accordion-pad">
              <form onSubmit={handleDocSubmit} style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                <label className="field-stack" style={{ marginBottom: 0 }}>
                  <span style={{ fontSize: 11 }}>Document name</span>
                  <input value={docType} onChange={(e) => setDocType(e.target.value)} placeholder="additional_document" />
                </label>
                <label className="file-upload-field lj-file-pick">
                  <DocumentIcon />
                  <span>{selectedFiles.length ? selectedFiles.map((f) => f.name).join(", ") : "Choose files"}</span>
                  <input accept={DOCUMENT_ALLOWED_EXTENSIONS.map((e) => `.${e}`).join(",")} multiple onChange={handleDocSelection} type="file" />
                </label>
                {(localDocErr || documentFeedback.error) && (
                  <p className="message-banner error" style={{ margin: 0 }}>{localDocErr || documentFeedback.error}</p>
                )}
                {documentFeedback.notice && (
                  <p className="message-banner success" style={{ margin: 0 }}>{documentFeedback.notice}</p>
                )}
                <button className="primary-button modal-submit" disabled={!remainingSlots || documentUploadLoading} type="submit">
                  {documentUploadLoading ? "Uploading…" : "Upload"}
                </button>
              </form>
              {documents.map((doc) => (
                <a key={doc.id} className="lj-doc-link-row" href={doc.file_url} rel="noreferrer" target="_blank">
                  <DocumentIcon />
                  <span>{doc.name}</span>
                  <span className="lj-doc-date">{formatDateCell(doc.created_at)}</span>
                </a>
              ))}
            </div>
          </Accordion>

          {/* TAGS */}
          <Accordion icon={TagIcon} title="Tags" badge={tags.length || null} defaultOpen={false}>
            <TagsSection tags={tags} leadId={lead.id} onUpdateTags={onUpdateTags} />
          </Accordion>

          {/* CUSTOM FIELDS */}
          {cfEntries.length > 0 && (
            <Accordion icon={NoteIcon} title="Custom Fields" defaultOpen={false}>
              <div className="lj-cf-grid">
                {cfEntries.map(([k, v]) => (
                  <div className="lj-cf-row" key={k}>
                    <span className="lj-cf-key">{startCase(k.replace(/_/g, " "))}</span>
                    <span className="lj-cf-val">{v == null || v === "" ? "—" : String(v)}</span>
                  </div>
                ))}
              </div>
            </Accordion>
          )}

          {/* ASSIGNMENT HISTORY */}
          {assignments.length > 0 && (
            <Accordion icon={TransferIcon} title="Assignment History" defaultOpen={false}>
              {assignments.map((a) => (
                <div className="lj-fu-row" key={a.id}>
                  <span className="lj-fu-badge">Transfer</span>
                  <div className="lj-fu-row-info">
                    <span className="lj-fu-row-title">{a.assigned_from_name || "—"} → {a.assigned_to_name}</span>
                    <span className="lj-fu-row-date">{formatDateCell(a.created_at)}</span>
                  </div>
                </div>
              ))}
            </Accordion>
          )}

          {/* DUPLICATES */}
          {duplicates.length > 0 && (
            <Accordion icon={UserGroupIcon} title="Duplicate Network" badge={duplicates.length} defaultOpen={false}>
              {duplicates.map((d) => (
                <div className="lj-fu-row" key={`dup-${d.id}`}>
                  <span className="lj-fu-badge lj-fu-duplicate">{d.client_id}</span>
                  <div className="lj-fu-row-info">
                    <span className="lj-fu-row-title">{d.full_name}</span>
                    <span className="lj-fu-row-date">{d.city_name} · {startCase(d.status)}</span>
                  </div>
                </div>
              ))}
            </Accordion>
          )}

          <div style={{ height: 24 }} />
        </div>{/* end lj-scroll-body */}

        <StageDetailsModal
          lead={lead}
          activities={activities}
          callLogs={callLogs}
          step={selectedStep}
          onClose={() => setSelectedStep(null)}
        />
      </div>
    </div>
  );
}

export default LeadJourneyModal;
